import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

import { FirebaseCollections } from '../../../constants/firebase.constants'
import { getDb, getFirebaseFunctions, getFirebaseStorage } from '../../../services/firebase.service'
import { ConfigurationCategory } from '../types/configuration.types'

import type { BrandingConfigurationType } from '../../../branding/types'
import type { BeachPlanConfigurationType } from '../BeachPlanConfiguration/types/types'
import type { ConfigurationData, ConfigurationType } from '../types/configuration.types'
import type { GeneralConfigurationType } from '../GeneralConfiguration/types/types'
import type { AvailabilityConfigurationType } from '../AvailabilityConfiguration/types/availability.types'
import type { PricingConfigurationType } from '../PricingConfiguration/types/pricingConfiguration.types'
import type { PaymentConfigurationType } from '../PaymentConfiguration/types/types'
import type { EmailConfigurationType } from '../EmailConfiguration/types/emailConfiguration.types'
import type { FeaturesConfigurationType } from '../FeaturesConfiguration/types/featuresConfiguration.types'

const ensureConfigurationDoc = async (type: ConfigurationType): Promise<void> => {
  const functions = await getFirebaseFunctions()
  const ensureConfigurationCallable = httpsCallable<{ type: ConfigurationType }, Record<string, unknown>>(functions, 'ensureConfiguration')
  await ensureConfigurationCallable({ type })
}

export const getConfiguration = async <T extends ConfigurationData>(type: ConfigurationType): Promise<T> => {
  const db = await getDb()
  const docRef = doc(db, FirebaseCollections.CONFIGURATION, type)
  let docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    await ensureConfigurationDoc(type)
    docSnap = await getDoc(docRef)
  }

  if (!docSnap.exists()) {
    throw new Error(`Configuration "${type}" could not be loaded`)
  }

  return docSnap.data() as T
}

export const updateConfiguration = async <T extends ConfigurationData>(type: ConfigurationType, data: Partial<T>): Promise<void> => {
  const db = await getDb()
  const docRef = doc(db, FirebaseCollections.CONFIGURATION, type)

  await setDoc(
    docRef,
    {
      ...data,
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  )
}

// --------------------------------- Upload Image ---------------------------------
const createUniqueFileName = (file: File) => {
  const fileExtension = file.name.split('.').pop()
  const fileNameWithoutExtension = file.name.replace(`.${fileExtension}`, '')
  const uniqueFileName = `${fileNameWithoutExtension}-${Date.now()}.${fileExtension}`
  return uniqueFileName
}

type UploadImageOptions = {
  cacheControl?: string
}

export const uploadImage = async (file: File, type: ConfigurationType, options?: UploadImageOptions): Promise<string> => {
  const storage = await getFirebaseStorage()
  const uniqueFileName = createUniqueFileName(file)
  const storageRef = ref(storage, `configurations/${type}/${uniqueFileName}`)
  await uploadBytes(storageRef, file, options?.cacheControl ? { cacheControl: options.cacheControl } : undefined)
  return await getDownloadURL(storageRef)
}

//--------------------------------- Helper functions for specific configuration types ---------------------------------
export const getBeachPlanConfiguration = () => getConfiguration<BeachPlanConfigurationType>(ConfigurationCategory.BEACH_PLAN)
export const getGeneralConfiguration = () => getConfiguration<GeneralConfigurationType>(ConfigurationCategory.GENERAL)
export const getEmailConfiguration = () => getConfiguration<EmailConfigurationType>(ConfigurationCategory.EMAIL)
export const getAvailabilityConfiguration = () => getConfiguration<AvailabilityConfigurationType>(ConfigurationCategory.AVAILABILITY)
export const getPricingConfiguration = () => getConfiguration<PricingConfigurationType>(ConfigurationCategory.PRICING)
export const getPaymentConfiguration = () => getConfiguration<PaymentConfigurationType>(ConfigurationCategory.PAYMENT)
export const getBrandingConfiguration = () => getConfiguration<BrandingConfigurationType>(ConfigurationCategory.BRANDING)
export const getFeaturesConfiguration = () => getConfiguration<FeaturesConfigurationType>(ConfigurationCategory.FEATURES)

export const updateBeachPlanConfiguration = (data: Partial<BeachPlanConfigurationType>) => updateConfiguration(ConfigurationCategory.BEACH_PLAN, data)
export const updateGeneralConfiguration = (data: Partial<GeneralConfigurationType>) => updateConfiguration(ConfigurationCategory.GENERAL, data)
export const updateAvailabilityConfiguration = (data: Partial<AvailabilityConfigurationType>) =>
  updateConfiguration(ConfigurationCategory.AVAILABILITY, data)
export const updatePricingConfiguration = (data: Partial<PricingConfigurationType>) => updateConfiguration(ConfigurationCategory.PRICING, data)
export const updatePaymentConfiguration = (data: Partial<PaymentConfigurationType>) => updateConfiguration(ConfigurationCategory.PAYMENT, data)
export const updateEmailConfiguration = (data: Partial<EmailConfigurationType>) => updateConfiguration(ConfigurationCategory.EMAIL, data)
export const updateBrandingConfiguration = (data: Partial<BrandingConfigurationType>) => updateConfiguration(ConfigurationCategory.BRANDING, data)
export const updateFeaturesConfiguration = (data: Partial<FeaturesConfigurationType>) => updateConfiguration(ConfigurationCategory.FEATURES, data)

export interface GeneralConfiguration {
  [key: string]: unknown
}

export interface PricingConfiguration {
  [key: string]: unknown
}

export interface AvailabilityConfiguration {
  [key: string]: unknown
}

export interface PaymentConfiguration {
  [key: string]: unknown
}

export interface EmailConfiguration {
  [key: string]: unknown
}

export interface Configuration {
  id: ConfigurationType
  beachPlan?: BeachPlanConfigurationType
  general?: GeneralConfigurationType
  pricing?: PricingConfiguration
  availability?: AvailabilityConfigurationType
  payment?: PaymentConfigurationType
  email?: EmailConfigurationType
  updatedAt: Date
}

export const configurationService = {
  async getConfiguration(category: ConfigurationType): Promise<Configuration | null> {
    try {
      const data = await getConfiguration<ConfigurationData>(category)
      return { id: category, ...data, updatedAt: new Date() } as Configuration
    } catch {
      return null
    }
  },

  async updateConfiguration(category: ConfigurationType, data: Partial<Configuration>): Promise<void> {
    await updateConfiguration(category, data as Partial<ConfigurationData>)
  },
}
